import type {
  Configuration,
  CreateMPAPolicyRequest,
  CreateMPARequestReviewRequest,
  ListMPARequestsRequest,
  ListMyMPARequestsRequest,
  MPAOperationType,
  MPARequest,
  UpdateMPAPolicyRequest,
} from '@repo/api-gw-sdk';
import { MpaApi } from '@repo/api-gw-sdk';
import type { SWRConfiguration } from 'swr';

import type { HttpClient } from './httpClient';

const baseUrl = '/mpa';

export const mpa = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const mpaApi = new MpaApi(config);

  return {
    operations: {
      useList: () =>
        httpClient.execute(
          mpaApi.listMPAOperations.bind(mpaApi),
          baseUrl,
          'operations'
        ),
    },
    policies: {
      useList: (operation?: MPAOperationType) =>
        httpClient.execute(
          mpaApi.listMPAPolicies.bind(mpaApi),
          baseUrl,
          'policies',
          currentProjectId,
          operation
        ),
      create: (body: CreateMPAPolicyRequest) =>
        mpaApi.createMPAPolicy(currentProjectId, body),
      update: (mpaPolicyId: string, body: UpdateMPAPolicyRequest) =>
        mpaApi.updateMPAPolicy(currentProjectId, mpaPolicyId, body),
      delete: (mpaPolicyId: string) =>
        mpaApi.deleteMPAPolicy(currentProjectId, mpaPolicyId),
    },
    myRequests: {
      useList: (
        body?: ListMyMPARequestsRequest,
        pageToken?: string,
        pageSize?: number,
        swrOptions?: SWRConfiguration,
        enabled = true
      ) =>
        httpClient.executeWithConfig(
          mpaApi.listMyMPARequests.bind(mpaApi),
          enabled ? baseUrl : null,
          'my-requests',
          swrOptions ?? {},
          currentProjectId,
          pageToken,
          pageSize,
          body
        ),
      useGet: (requestId: string | null) =>
        httpClient.execute(
          mpaApi.getMyMPARequest.bind(mpaApi),
          requestId ? baseUrl : null,
          `my-requests/${requestId}`,
          currentProjectId,
          requestId ?? ''
        ),
      cancel: (requestId: string) =>
        mpaApi.cancelMyMPARequest(currentProjectId, requestId),
      // Replay the original API call captured on the MPA request, tagged with
      // the request id so the backend MPA middleware transitions it from
      // APPROVED to EXECUTED and lets the original handler run.
      apply: (request: MPARequest) => {
        if (!request.requestMethod || !request.requestPath) {
          throw new Error('MPA request is missing method or path');
        }
        const headers: Record<string, string> = {
          'X-MPA-Request-Id': request.id,
        };
        if (request.requestBody) {
          headers['Content-Type'] = 'application/json';
        }
        return httpClient.request(request.requestMethod, request.requestPath, {
          body: request.requestBody ?? undefined,
          headers,
        });
      },
    },
    requests: {
      useList: (
        body?: ListMPARequestsRequest,
        pageToken?: string,
        pageSize?: number,
        swrOptions?: SWRConfiguration,
        enabled = true
      ) =>
        httpClient.executeWithConfig(
          mpaApi.listMPARequests.bind(mpaApi),
          enabled ? baseUrl : null,
          'requests',
          swrOptions ?? {},
          currentProjectId,
          pageToken,
          pageSize,
          body
        ),
      useGet: (requestId: string | null) =>
        httpClient.execute(
          mpaApi.getMPARequest.bind(mpaApi),
          requestId ? baseUrl : null,
          `requests/${requestId}`,
          currentProjectId,
          requestId ?? ''
        ),
      review: (requestId: string, body: CreateMPARequestReviewRequest) =>
        mpaApi.createMPARequestReview(currentProjectId, requestId, body),
    },
  };
};
